/**
 * PromoImageDropzone — drag-and-drop image uploader for promo flyers.
 *
 * Sits inside PromoSettingsCard. Uploads to
 * `POST /api/admin/promo/<key>/upload-image` (multipart). Server saves
 * to `/promo/uploads/<key>-<kind>-<ts>.<ext>` and updates the promo's
 * `image_path` (kind=flyer) or `og_image_path` (kind=og).
 *
 * Accessible: clickable label + drop zone, keyboard-focusable, screen
 * reader announces upload state via aria-live.
 */
import React, { useCallback, useRef, useState } from 'react';
import { Upload, RefreshCw, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';
const MAX_BYTES = 5 * 1024 * 1024;

export function PromoImageDropzone({ promoKey, kind = 'flyer', currentPath, onUploaded }) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const previewSrc = currentPath ? `${currentPath}?t=${Date.now()}` : null;

  const upload = useCallback(async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`File too large (${(file.size / 1048576).toFixed(1)} MB). Max 5 MB.`);
      return;
    }

    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);

    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/admin/promo/${promoKey}/upload-image`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.detail || 'Upload failed');
      }
      toast.success(`${kind === 'og' ? 'OG card' : 'Flyer'} updated`);
      onUploaded?.(data);
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [promoKey, kind, onUploaded]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };

  return (
    <div className="space-y-2" data-testid={`promo-dropzone-${promoKey}-${kind}`}>
      <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {kind === 'og' ? 'OG / Twitter card (1200×630)' : 'Flyer image (300×400)'}
      </label>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
        role="button"
        tabIndex={0}
        aria-live="polite"
        className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4 cursor-pointer transition-colors ${
          dragOver
            ? 'border-[#00E5CC] bg-[#00E5CC]/5'
            : 'border-muted-foreground/30 hover:border-[#00E5CC]/60 hover:bg-muted/40'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid={`promo-dropzone-input-${promoKey}-${kind}`}
        />
        {uploading ? (
          <>
            <RefreshCw className="h-6 w-6 animate-spin text-[#00A896] dark:text-[#00E5CC]" />
            <span className="text-sm text-muted-foreground">Uploading…</span>
          </>
        ) : previewSrc ? (
          <>
            <img
              src={previewSrc}
              alt={`${kind} preview`}
              className="max-h-32 rounded border bg-white object-contain"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <span className="text-xs text-muted-foreground">
              <Upload className="inline h-3 w-3 mr-1" /> Drop or click to replace
            </span>
          </>
        ) : (
          <>
            <ImageIcon className="h-6 w-6 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              <Upload className="inline h-3 w-3 mr-1" /> Drop or click to upload
            </span>
            <span className="text-[10px] text-muted-foreground">PNG / JPG / WEBP · max 5 MB</span>
          </>
        )}
      </div>
    </div>
  );
}

export default PromoImageDropzone;
